import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { openReportInNewTab } from '@ltvco/refresh-lib/hooks';
import { useStates } from '@ltvco/refresh-lib/utils';
import {
  AdvancedSearchURLParams,
  CollectionType,
  PersonSearchParamsv2,
  RelatedReport,
} from '@ltvco/refresh-lib/v2';
import { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { constants } from 'appConstants';
import {
  CHATBV_GA4_EVENTS,
  NULL_SEARCH_PARAMS,
  SEARCH_TYPES,
} from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import {
  GenericReportResponse,
  Message,
  MessageReportData,
} from 'components/ChatBV/types';
import {
  getPersonPermalink,
  getRelatedReportPermalink,
  TransformedReportData,
  getRelatedReportData,
} from 'components/ChatBV/utils';

export const useReportState = () => {
  const [openReport, setOpenReport] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [permalink, setPermalink] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<
    GenericReportResponse | undefined
  >(undefined);
  const [type, setType] = useState<string | undefined>(undefined);
  const { trackEventGA4, logError } = useContext(AppConfig);
  const [bvid, setBvid] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] =
    useState<AdvancedSearchURLParams>(NULL_SEARCH_PARAMS);
  const [collectionType, setCollectionType] = useState<CollectionType>(
    CollectionType.PUBLIC_RECORDS
  );
  const states = useStates();
  const location = useLocation();

  const { messages } = useContext(ChatContext);

  const closeAllPanels = () => {
    setOpenReport(false);
    setOpenFilters(false);
    setPermalink(undefined);
    setReportData(undefined);
    setType(undefined);
    setBvid(undefined);
  };

  // Don't close panels when on report library page, as reports can be opened without messages
  const isOnReportLibrary = location.pathname.includes('chat/library');

  useEffect(() => {
    // Only close panels if we're not on the report library page
    // On the library page, reports can be opened independently of messages
    if (messages.length === 0 && !isOnReportLibrary) {
      closeAllPanels();
    }
  }, [messages.length, isOnReportLibrary, closeAllPanels]);

  const openReportFromReportLibrary = (permalink: string, type: string) => {
    if (!permalink || !type) {
      logError(
        'Error opening report from library: missing permalink or type',
        new Error(`permalink: ${permalink}, type: ${type}`)
      );
      return;
    }
    setPermalink(permalink);
    setReportData(undefined);
    setType(type);
    setOpenReport(true);
    setOpenFilters(false);
  };
  const handleButtonClick =
    //TODO: check if we need this arg in the function
    // eslint-disable-next-line no-unused-vars
    (_message: Message) => (report: MessageReportData) => {
      if (report) {
        setPermalink(report.permalink);
        setReportData(report.reportData);
        setType(report.type);
        setOpenReport(true);
        setOpenFilters(false);
      }
    };

  const handleCloseReport = () => {
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.report.type,
      subtype: CHATBV_GA4_EVENTS.report.subTypes.close_report,
    });
    closeAllPanels();
  };

  const handleChatTeaserClick =
    (typeOverride?: string) => async (bvid: string) => {
      if (bvid) {
        setOpenReport(true);
        setOpenFilters(false);
        setBvid(bvid);
        try {
          const { error, permalink } = await getPersonPermalink(bvid);
          if (error) {
            throw new Error(`Error getting person permalink: ${error}`);
          }
          if (permalink) {
            setType(typeOverride || SEARCH_TYPES.PERSON || '');
            setPermalink(permalink);
          } else {
            throw new Error('No permalink found');
          }
        } catch (error) {
          closeAllPanels();
          logError('Error getting person permalink:', error as Error);
        }
      }
    };

  const ensureStateAbbreviation = (state: string) => {
    const stateName = state.toLocaleLowerCase();
    const stateObject = states.find(
      (state) =>
        state.abbr.toLocaleLowerCase() === stateName ||
        state.name.toLocaleLowerCase() === stateName
    );
    if (stateObject) {
      return stateObject.abbr;
    }
    return '';
  };

  const setParamsFromMessage = (message: Message) => {
    const newParams = {
      ...NULL_SEARCH_PARAMS,
      fName: message.params?.first_name || '',
      lName: message.params?.last_name || '',
      mName: message.params?.middle_name || '',
      fullName: [
        message.params?.first_name,
        message.params?.middle_name,
        message.params?.last_name,
      ]
        .filter(Boolean)
        .join(' '),
      birthMonth: message.params?.birth_month || '',
      birthYear: message.params?.birth_year || '',
      deathMonth: message.params?.death_month || '',
      deathYear: message.params?.death_year || '',
      city: message.params?.city || '',
      state: message.params?.state
        ? ensureStateAbbreviation(message.params?.state)
        : '',
      age: message.params?.age || '',
      company: message.params?.company || '',
      industry: message.params?.industry || '',
      title: message.params?.title || '',
      middle_name: message.params?.middle_name || '',
    };
    setSearchParams({ ...searchParams, ...newParams });
  };

  const setParamsFromPersonSearchParams = (params: PersonSearchParamsv2) => {
    setSearchParams({ ...NULL_SEARCH_PARAMS, ...params });
  };

  const handleFiltersClick =
    (message: Message) => (params?: PersonSearchParamsv2) => {
      if (params) {
        setParamsFromPersonSearchParams(params);
      } else {
        setParamsFromMessage(message);
      }
      setOpenFilters(true);
      setOpenReport(false);
    };

  const handleCloseFilters = () => {
    closeAllPanels();
  };

  const handleButtonClickFromFollowUpMessage = (message: Message) => () => {
    setPermalink(message.permalink);
    setType('person');
    setOpenReport(true);
    setOpenFilters(false);
  };

  const handleObituaryClick = (permalink: string) => {
    setPermalink(permalink);
    setType('obituary');
    setOpenReport(true);
    setOpenFilters(false);
  };

  const handleAncestorClick = (permalink: string) => {
    setPermalink(permalink);
    setType('ancestor');
    setOpenReport(true);
    setOpenFilters(false);
  };

  const handleContactClick = (permalink: string) => {
    setPermalink(permalink);
    setType('contact');
    setOpenReport(true);
    setOpenFilters(false);
  };

  const handleRelatedReportClick = async (relatedReport: RelatedReport) => {
    setOpenReport(false);
    setOpenFilters(false);

    try {
      const transformedData = getRelatedReportData(relatedReport);

      if (transformedData.reportType === 'teaser') {
        if (transformedData.personSearch) {
          setParamsFromPersonSearchParams(transformedData.personSearch);
        }
        return;
      }

      if (transformedData.useBvid && transformedData.data?.bvid) {
        setOpenReport(true);
        setBvid(transformedData.data.bvid);

        const { error, permalink } = await getPersonPermalink(
          transformedData.data.bvid
        );
        if (error) {
          throw new Error(`Error getting person permalink: ${error}`);
        }
        if (permalink) {
          setType(SEARCH_TYPES.PERSON);
          setPermalink(permalink);
        } else {
          throw new Error('No permalink found');
        }
        return;
      }

      if (
        transformedData.reportType === SEARCH_TYPES.OBITUARY &&
        transformedData.data?.permalink
      ) {
        // Handle obituary with direct permalink
        setPermalink(transformedData.data.permalink as string);
        setType(SEARCH_TYPES.OBITUARY);
        setOpenReport(true);
        return;
      }

      if (
        transformedData.reportType === SEARCH_TYPES.USERNAME &&
        transformedData.data?.username
      ) {
        openReportInNewTab(
          {
            username: transformedData.data.username,
            searchType: 'username',
          },
          constants.links.baseUrl
        );
        return;
      }

      // Handle other report types (phone, email, property, vehicle)
      if (transformedData.data) {
        setOpenReport(true);

        const permalinkResult = await getRelatedReportPermalink(
          transformedData.reportType,
          transformedData.data as TransformedReportData
        );

        if (permalinkResult?.permalink) {
          setType(transformedData.reportType);
          setPermalink(permalinkResult.permalink);
          setReportData(undefined);
        } else {
          throw new Error(`No ${transformedData.reportType} permalink found`);
        }
      }
    } catch (error) {
      closeAllPanels();
      logError(`Error handling related report click:`, error as Error);
    }
  };

  return {
    openReportFromReportLibrary,
    openReport,
    setOpenReport,
    openFilters,
    setOpenFilters,
    searchParams,
    setSearchParams,
    collectionType,
    setCollectionType,
    permalink,
    reportData,
    type,
    bvid,
    handleButtonClick,
    handleCloseReport,
    handleChatTeaserClick,
    handleButtonClickFromFollowUpMessage,
    handleFiltersClick,
    handleCloseFilters,
    handleObituaryClick,
    handleAncestorClick,
    handleContactClick,
    handleRelatedReportClick,
    closeAllPanels,
  };
};
